import { useEffect } from 'react';

type Subscription = {
  [key: string | number]: Array<Function>;
};

const subscriptions: Subscription = {};
const currentData: any = {};

const publish = (topic: string, data: any): void => {
  // Set the current data for the topic in the global store
  currentData[topic] = data;

  // If no subscriptions exist for the topic, exit
  if (!subscriptions[topic]?.length) return;

  // Publish the data to each subscription handler
  for (let handler of subscriptions[topic]) handler?.(data);
};

const getCurrentData = (topic: string): any => currentData[topic];

const subscribe = (topic: string, handler: Function): void => {
  let topicHandlers = subscriptions[topic];

  // If no handlers exist for the topic yet, initialize the topic
  if (!topicHandlers) {
    topicHandlers = [];
    subscriptions[topic] = topicHandlers;
  }

  // If the handler has already been registered for the topic,
  // exit to prevent duplicate regstrations
  if (topicHandlers.filter((h) => h === handler)[0]) return;

  // Register the handler for the topic
  topicHandlers.push(handler);
};

const unsubscribe = (topic: string, handler: Function): void => {
  let topicHandlers = subscriptions[topic];

  // If no subscriptions exist for the topic, exit
  if (!topicHandlers?.length) return;

  const index = topicHandlers.indexOf((h: Function) => h === handler);

  // If the handler isn't registered for the topic, exit
  if (index < 0) return;

  // Remove the handler from the topic
  topicHandlers.splice(index, 1);
};

const useSubscription = (topic: string, handler: Function): void => {
  useEffect(() => {
    subscribe(topic, handler);

    return () => {
      unsubscribe(topic, handler);
    };
  }, [handler, topic]);
};

export { useSubscription, publish, getCurrentData };
